import * as moment from 'moment';

import type Address from '~/types/Address';
import type Rate from '~/types/Rate';
import type Track from '~/types/Track';

import { CANADA_POST } from '~/constants/carriers';
import { type TransactionCreatedBy } from '~/pages/Shipments/ShipmentsPage/ShipmentsTable/types';
import * as APIMessage from '~/types/APIMessage';
import * as address from '~/types/Address';
import { type BaseWebOrder } from '~/types/Order';
import { type BaseWebShipment } from '~/types/OrderBatchEntry';
import * as rate from '~/types/Rate';
import { TrackStatus, type JSONTrack } from '~/types/Track';
import { newRelic } from '~/utils/newRelic/newRelic';

enum TransactionStatus {
  ERROR = 'ERROR',
  QUEUED = 'QUEUED',
  REFUNDED = 'REFUNDED',
  REFUNDPENDING = 'REFUNDPENDING',
  REFUNDREJECTED = 'REFUNDREJECTED',
  SUCCESS = 'SUCCESS',
  WAITING = 'WAITING',
}

enum ObjectState {
  INVALID = 'INVALID',
  VALID = 'VALID',
}

export type TransactionToRefund = {
  hasInsurance: boolean;
  isRefundable: boolean;
  transactionId: string;
};

type DeprecatedJSONTransactionProperties = {
  object_status?: string;
  was_test?: boolean;
};

type JSONTransaction = {
  billing: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payments: any[];
  };
  commercial_invoice_url: Maybe<string>;
  eta: Maybe<string>;
  insurance: {
    amount: string;
    claim_id: string;
    currency: string;
    provider: string;
  };
  is_downloadable?: boolean;
  is_refundable?: boolean;
  is_return?: boolean;
  label_url: string;
  messages: APIMessage.JSONAPIMessage[];
  metadata: string;
  object_created: string;
  object_id: string;
  object_owner: string;
  object_state: string;
  object_status: string;
  object_updated: string;
  order: Maybe<string>;
  parcel: string;
  rate: string;
  status: string;
  test: boolean;
  tracking_number: string;
  tracking_status: string;
  tracking_url_provider: string;
} & DeprecatedJSONTransactionProperties;

type Transaction = {
  billing: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payments: any[];
  };
  commercialInvoiceUrl: Maybe<string>;
  eta: Maybe<string>;
  insuranceAmount?: null | string;
  insuranceClaimId?: null | string;
  insuranceCurrency?: null | string;
  insuranceProvider?: null | string;
  isDownloadable?: boolean;
  isRefundable?: boolean;
  isReturn?: boolean;
  labelUrl: string; // TODO: This is wrong. Should actually be Maybe<string>
  messages: APIMessage.APIMessage[];
  metadata: string;
  objectCreated: string;
  objectId: string;
  objectOwner: string;
  objectState: ObjectState;
  objectUpdated: string;
  order: Maybe<string>;
  parcel: string;
  rate: string;
  status: TransactionStatus;
  test: boolean;
  trackingNumber: string;
  trackingStatus: TrackStatus;
  trackingUrlProvider: string;
};

// Clean this up in the future; maps to the TransactionSerializerUI2 in Django
// only used for shipment modal on all orders page as of now
type JSONTransactionUI2 = {
  address_to: address.JSONAddress;
  object_created: string;
  object_id: string;
  object_status: string;
  order_number: string;
  rate: rate.JSONRate;
  tracking_number: string;
  tracking_status: Track[];
  tracking_url_provider: string;
  was_test: boolean;
};

type TransactionUI2 = {
  addressTo: Maybe<Address>;
  objectCreated: moment.Moment;
  objectId: string;
  objectStatus: TransactionStatus;
  orderNumber: string;
  rate: Rate;
  trackingNumber: string;
  trackingStatus: Track[];
  trackingUrlProvider: string;
  wasTest: boolean;
};

type WebTransactionMessage = {
  code: string;
  text: string;
};

export type WebTransaction = {
  commercial_invoice_url: string;
  created_by?: TransactionCreatedBy | null;
  entry_point: string;
  eta: string;
  is_downloadable?: boolean;
  is_refundable?: boolean;
  is_return?: boolean;
  label_url: string;
  messages: WebTransactionMessage[];
  metadata: string;
  object_created: Date;
  object_id: string;
  object_owner: string;
  object_state: ObjectState;
  object_status: string;
  object_updated: Date;
  packing_slip_url: string;
  rate: rate.WebRate;
  refund_request_date: string;
  tracking_number: string;
  tracking_url_provider: string;
  was_test: boolean;
};

export type WebTransactionExtended = {
  eta?: string;
  order: BaseWebOrder;
  pickup_date?: Date;
  shipment: BaseWebShipment;
  tracking_status: JSONTrack;
} & WebTransaction;

/**
 * Handles legacy label URLs that come directly from S3, redirecting them to use deliver.goshippo.com
 */
const getLabelUrl = (labelUrl?: string): string => {
  try {
    if (!labelUrl) return '';

    const parsedUrl = new URL(labelUrl);
    const parts = window.location.host.split('.');
    const [subdomain] = parts;

    if (parsedUrl.host.startsWith('local')) return labelUrl;
    if (parsedUrl.host.startsWith('stage-perf')) return labelUrl;
    const { hostname } = parsedUrl;
    const isS3Url = hostname === 'amazonaws.com' || hostname.endsWith('.amazonaws.com');
    if (!isS3Url) return labelUrl;

    const newSubdomain = subdomain.replace('apps', 'deliver');
    const newHost = `${newSubdomain}.${parts[1]}.${parts[2].split(':')[0]}`;
    const path = parsedUrl.pathname;
    const qs = parsedUrl.search;
    const isWindows = /Windows/.test(window.navigator.userAgent);
    const extParam = isWindows && path.includes('.pdf') ? '&ext=.pdf' : '';

    return `https://${newHost}${path}${qs}${extParam}`;
  } catch (error) {
    newRelic.noticeError('Failed to parse label URL', { error, labelUrl });
    return '';
  }
};

const fromJSON = (jsonTransaction: JSONTransaction): Transaction => {
  const test = (jsonTransaction.test ? jsonTransaction.test : jsonTransaction.was_test) || false;

  const { amount } = jsonTransaction.insurance || {};
  const insuranceAmount = amount === '0' ? undefined : amount;

  return {
    billing: jsonTransaction.billing,
    commercialInvoiceUrl: jsonTransaction.commercial_invoice_url,
    eta: jsonTransaction.eta,
    insuranceAmount,
    insuranceClaimId: jsonTransaction?.insurance?.claim_id,
    insuranceCurrency: jsonTransaction?.insurance?.currency,
    insuranceProvider: jsonTransaction?.insurance?.provider,
    isDownloadable: jsonTransaction.is_downloadable,
    isRefundable: jsonTransaction.is_refundable,
    isReturn: jsonTransaction.is_return,
    labelUrl: getLabelUrl(jsonTransaction.label_url),
    messages: (jsonTransaction.messages || []).map(APIMessage.fromJSON),
    metadata: jsonTransaction.metadata,
    objectCreated: jsonTransaction.object_created,
    objectId: jsonTransaction.object_id,
    objectOwner: jsonTransaction.object_owner,
    objectState: <ObjectState>jsonTransaction.object_state,
    objectUpdated: jsonTransaction.object_updated,
    order: jsonTransaction.order,
    parcel: jsonTransaction.parcel,
    rate: jsonTransaction.rate,
    status:
      <TransactionStatus>jsonTransaction.status || <TransactionStatus>jsonTransaction.object_status,
    test,
    trackingNumber: jsonTransaction.tracking_number,
    trackingStatus: <TrackStatus>jsonTransaction.tracking_status,
    trackingUrlProvider: jsonTransaction.tracking_url_provider,
  };
};

export const fromWebTransaction = (webTransaction: WebTransaction): Transaction => {
  const messages = webTransaction.messages.map(
    ({ code, text }): APIMessage.APIMessage => ({
      code,
      kind: 'APIMessage',
      source: '',
      text,
    }),
  );

  return {
    billing: {
      payments: [],
    },
    commercialInvoiceUrl: webTransaction.commercial_invoice_url,
    eta: webTransaction.eta,
    labelUrl: webTransaction.label_url,
    messages,
    metadata: webTransaction.metadata,
    objectCreated: webTransaction.object_created.toISOString(),
    objectId: webTransaction.object_id,
    objectOwner: webTransaction.object_owner,
    objectState: <ObjectState>webTransaction.object_state,
    objectUpdated: webTransaction.object_updated.toISOString(),
    order: undefined,
    parcel: '',
    rate: webTransaction.rate?.object_id,

    status: <TransactionStatus>webTransaction.object_status,
    test: webTransaction.was_test,
    trackingNumber: webTransaction.tracking_number,
    trackingStatus: TrackStatus.UNKNOWN,
    trackingUrlProvider: webTransaction.tracking_url_provider,
  };
};

const transactionUI2FromJSON = (jsonTransaction: JSONTransactionUI2): TransactionUI2 => ({
  addressTo: address.fromJSON(jsonTransaction.address_to),
  objectCreated: moment(jsonTransaction.object_created),
  objectId: jsonTransaction.object_id,
  objectStatus: <TransactionStatus>jsonTransaction.object_status,
  orderNumber: jsonTransaction.order_number,
  rate: rate.fromJSON(jsonTransaction.rate),
  trackingNumber: jsonTransaction.tracking_number,
  trackingStatus: jsonTransaction.tracking_status,
  trackingUrlProvider: jsonTransaction.tracking_url_provider,
  wasTest: jsonTransaction.was_test,
});

const hasTransactionError = (transaction: Pick<Transaction, 'messages' | 'status'>) =>
  transaction.status === TransactionStatus.ERROR && transaction.messages.length > 0;

const isRefundIneligible = (transaction: TransactionUI2) =>
  transaction.rate.provider === CANADA_POST.name &&
  transaction.objectCreated.isAfter(moment().subtract(60, 'minute'));

const isIneligibleForPaperlessTrade = (transaction: Transaction) =>
  !!transaction.commercialInvoiceUrl &&
  transaction.messages.length > 0 &&
  !!transaction.messages.find((x) => x.text.includes('Not eligible for paperless trade.'));

export default Transaction;
export {
  JSONTransaction,
  JSONTransactionUI2,
  ObjectState,
  Transaction,
  TransactionStatus,
  TransactionUI2,
  fromJSON,
  getLabelUrl,
  hasTransactionError,
  isIneligibleForPaperlessTrade,
  isRefundIneligible,
  transactionUI2FromJSON,
};
